import { useMemo } from "react";
import { useFirestore } from "@/hooks/firestore/core/useFirestore";
import { skrillDeposit } from "@/hooks/firestore/models/skrillDeposit";
import { useProfile } from "@/hooks/firestore/user/useProfile";
import type { Always } from "types/utilities";

const useSkrillDeposits = () => {
  const {
    isLoading,
    data: deposits,
    error,
  } = useFirestore(`/users/{userId}/skrillDeposits`, skrillDeposit.array());

  return { deposits, error, isLoading };
};

const useSkrillDeposit = (transactionId: string | undefined) => {
  const {
    isLoading,
    data: deposit,
    error,
  } = useFirestore(
    transactionId && `/users/{userId}/skrillDeposits/${transactionId}`,
    skrillDeposit,
  );

  return { deposit, error, isLoading };
};

const useSkrillEmails = () => {
  const { deposits, isLoading: depositsLoading } = useSkrillDeposits();
  const { profile, isLoading: profileLoading } = useProfile();

  const emails = useMemo(() => {
    if (depositsLoading || !profile) {
      return [];
    }

    return [
      ...new Set([
        profile.email,
        ...(deposits || []).map((deposit) => deposit.skrillEmail),
      ]),
    ].filter(Boolean);
  }, [profile?.email, deposits]);

  return {
    emails,
    isLoading: depositsLoading || profileLoading,
  };
};

type SkrillDeposit = Always<
  ReturnType<typeof useSkrillDeposits>["deposits"]
>[number];

export { useSkrillDeposit, useSkrillDeposits, useSkrillEmails };
export type { SkrillDeposit };
